<template>
  <div class="plot-header">
    <span class="plot-header-label">
      {{ peopleId ? 'Payments for selected person' : 'All Payments' }}
    </span>
    <div
      v-if="!peopleId && currentCycleStats"
      class="plot-header-stats-row"
    >
      <button
        type="button"
        class="plot-stats-copy-btn"
        :title="statsCopyLabel"
        @click="copyCycleStats"
      >
        <i :class="['fas', statsCopied ? 'fa-check' : 'fa-copy']"></i>
      </button>
      <span class="plot-header-stats">
        <span
          class="cycle-stat"
          title="Unique addresses paid this cycle (share of properties, share of payments via PixieChatBot mobile)"
        >
          Payments: {{ currentCycleStats.paymentCount }}
          {{ currentCycleStats.paymentPercent }}
        </span>
        <span class="cycle-stat">Rcvd: {{ currentCycleStats.received }}</span>
        <span class="cycle-stat">
          Deposited: {{ currentCycleStats.deposited }}
        </span>
        <span
          class="cycle-stat cycle-stat-clickable"
          title="Sum of undeposited payments from current + previous cycle; double-click to multi-select those rows"
          @dblclick="$emit('select-clearing-payments')"
        >
          Clearing: {{ currentCycleStats.clearing }}
        </span>
        <span
          class="cycle-stat cycle-stat-clickable"
          title="Monthly rent for active leases not yet paid this cycle (25th–24th); double-click for list"
          @dblclick="$emit('show-uncollected-leases', 'rent')"
        >
          Uncollected: {{ currentCycleStats.uncollected }}
        </span>
        <span
          class="cycle-stat cycle-stat-clickable"
          title="Sum of negative lease balances; double-click for list"
          @dblclick="$emit('show-uncollected-leases', 'balance')"
        >
          Balances: {{ currentCycleStats.balances }}
        </span>
        <span
          class="cycle-stat"
          title="Sum of most recent lease rent for currently vacant rentable units"
        >
          Vacant: {{ currentCycleStats.vacant }}
        </span>
      </span>
    </div>
  </div>
  <div ref="plot" class="plot-container">
    <!-- Display "Please wait..." while loading is true -->
    <div v-if="loading" class="loading-message">
      Please wait for the chart to load...
    </div>
  </div>
</template>

<script>
import axios from '@/axiosConfig'; // Adjust based on your axios configuration
import Plotly from 'plotly.js-dist';

export default {
  name: 'PlotPayments',
  emits: ['show-uncollected-leases', 'select-clearing-payments'],
  props: {
    startDate: {
      type: String,
      required: true,
    },
    endDate: {
      type: String,
      required: true,
    },
    peopleId: {
      type: Number,
      required: false,
    },
    refreshPlot: {
      type: Boolean,
      required: false,
    },
    currentCycleStats: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      loading: true, // Initialize loading state to true
      paymentData: [],
      statsCopied: false,
      statsCopyResetTimer: null,
    };
  },
  computed: {
    statsCopyLabel() {
      return this.statsCopied ? 'Copied' : 'Copy cycle stats';
    },
  },
  mounted() {
    // Fetch the payment data when the component is mounted
    this.fetchPaymentData();
  },
  beforeUnmount() {
    if (this.statsCopyResetTimer) {
      clearTimeout(this.statsCopyResetTimer);
    }
  },
  methods: {
    buildCycleStatsClipboardText() {
      const stats = this.currentCycleStats;
      if (!stats) {
        return '';
      }

      return [
        `Payments: ${stats.paymentCount} ${stats.paymentPercent}`,
        `Rcvd: ${stats.received}`,
        `Deposited: ${stats.deposited}`,
        `Clearing: ${stats.clearing}`,
        `Uncollected: ${stats.uncollected}`,
        `Balances: ${stats.balances}`,
        `Vacant: ${stats.vacant}`,
      ].join('\n');
    },

    copyTextToClipboard(text) {
      if (navigator.clipboard && window.isSecureContext) {
        return navigator.clipboard.writeText(text);
      }

      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.setAttribute('readonly', '');
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      textArea.setSelectionRange(0, text.length);

      try {
        const copied = document.execCommand('copy');
        if (!copied) {
          throw new Error('execCommand copy failed');
        }
        return Promise.resolve();
      } finally {
        document.body.removeChild(textArea);
      }
    },

    markStatsCopied() {
      this.statsCopied = true;
      if (this.statsCopyResetTimer) {
        clearTimeout(this.statsCopyResetTimer);
      }
      this.statsCopyResetTimer = setTimeout(() => {
        this.statsCopied = false;
        this.statsCopyResetTimer = null;
      }, 1500);
    },

    async copyCycleStats() {
      const text = this.buildCycleStatsClipboardText();
      if (!text) {
        return;
      }

      try {
        await this.copyTextToClipboard(text);
        this.markStatsCopied();
      } catch (error) {
        console.error('Failed to copy cycle stats:', error);
      }
    },

    async fetchPaymentData() {
      this.loading = true; // Show loading indicator immediately
      Plotly.purge(this.$refs.plot); // Clear previous chart immediately

      try {
        // Start the API request and await its result non-blockingly
        const response = await axios.get('/api/payments-summary', {
          params: {
            startDate: this.startDate,
            endDate: this.endDate,
            peopleId: this.peopleId,
          },
        });

        let paymentData = response.data;

        console.log('plot payments: ', this.peopleId);
        if (this.peopleId) {
          // Create a Set of existing first-day entries
          const existingFirstDays = new Set(
            paymentData
              .filter((entry) => entry.day === 1)
              .map((entry) => entry.monthNum),
          );

          // Loop through months 1-12 and add a $0 entry for the first if missing
          for (let month = 1; month <= 12; month++) {
            if (!existingFirstDays.has(month)) {
              paymentData.push({
                year: new Date().getFullYear(),
                monthNum: month,
                day: 1,
                totalPayments: '0.00',
              });
            }
          }
        }

        // Sort by year, monthNum, and day
        paymentData.sort((a, b) => {
          if (a.year === b.year) {
            if (a.monthNum === b.monthNum) {
              return a.day - b.day;
            }
            return a.monthNum - b.monthNum;
          }
          return a.year - b.year; // Sort by year first
        });

        // Assign the modified and sorted data to `paymentData` property
        this.paymentData = paymentData;
      } catch (error) {
        console.error('Error fetching payment data:', error);
      } finally {
        this.loading = false; // Hide loading indicator once data is processed
      }

      // Prepare the data for the plot
      const xValues = this.paymentData.map(
        (item) =>
          `${String(item.monthNum).padStart(2, '0')}-${String(item.day).padStart(2, '0')}-${String(item.year).slice(-2)}`,
      );
      const yValues = this.paymentData.map((item) => item.totalPayments);

      // Create the bar chart using Plotly
      this.plotPayments(xValues, yValues);
    },

    async plotPayments(xValues, yValues) {
      const trace = {
        x: xValues,
        y: yValues,
        type: 'bar',
        hovertemplate:
          '<br><b style="text-align: center; display: block;">%{x}</b><br>$%{y:,.2f}<extra></extra>',
        marker: {
          color: '#baa2bd', // Optional: Customize the outline color of the bars if needed
          line: {
            color: '#907793', // Set the bar fill color to the specified color
            width: 1.5,
          },
        },
      };

      // Generate annotations for late and early payments
      const annotations = [];
      if (this.peopleId) {
        xValues.forEach((date, index) => {
          const day = parseInt(date.split('-')[1], 10); // Extract day from date formatted as MM/DD

          // Determine the label based on the day of the month
          let label = null;
          if (day > 5 && day < 25) {
            label = 'Late';
          } else if (day >= 25 && day <= 31) {
            label = 'Early?';
          }

          // Add annotation if label is set
          if (label) {
            annotations.push({
              x: date,
              y: yValues[index] / 2, // Position in the middle of the bar
              text: label,
              showarrow: false,
              font: {
                size: 12,
                color: '#fff', // Optional: Customize label color
              },
              textangle: -90, // Rotate text 90 degrees
            });
          }
        });
      }

      const layout = {
        margin: { t: 10, l: 40, r: 10, b: 30 },
        height: 195,
        hoverlabel: {
          font: { size: 11, color: '#eee' },
          bgcolor: '#333',
        },
        font: { size: 12, family: 'Arial, sans-serif' },
        xaxis: {
          type: 'category', // Treat the x values as categorical strings
          tickangle: -90,
          automargin: true,
        },
        yaxis: { automargin: true },
        annotations: annotations,
      };

      Plotly.newPlot(this.$refs.plot, [trace], layout, {
        displayModeBar: false,
      });
      this.loading = false;
    },
  },
  watch: {
    peopleId() {
      this.fetchPaymentData(); // Re-fetch payment data when startDate changes
    },

    refreshPlot(newVal) {
      // do this only on false -> true
      if (newVal) this.fetchPaymentData(); // Re-fetch payment data when startDate changes
    },

    startDate() {
      // Check if both startDate and endDate are valid dates
      const startDate = new Date(this.startDate);
      const endDate = new Date(this.endDate);

      if (
        !this.startDate ||
        !this.endDate ||
        isNaN(startDate.getTime()) ||
        isNaN(endDate.getTime())
      ) {
        return; // Exit the function if either date is invalid
      }

      // Check if startDate is earlier than endDate
      if (startDate >= endDate) {
        return; // Exit the function if startDate is not earlier than endDate
      }

      // If validation passes, fetch payments
      this.fetchPaymentData(); // Re-fetch payment data when startDate changes
    },
    endDate() {
      // Check if both startDate and endDate are valid dates
      const startDate = new Date(this.startDate);
      const endDate = new Date(this.endDate);

      if (
        !this.startDate ||
        !this.endDate ||
        isNaN(startDate.getTime()) ||
        isNaN(endDate.getTime())
      ) {
        return; // Exit the function if either date is invalid
      }

      // Check if startDate is earlier than endDate
      if (startDate >= endDate) {
        return; // Exit the function if startDate is not earlier than endDate
      }

      // If validation passes, fetch payments
      this.fetchPaymentData(); // Re-fetch payment data when endDate changes
    },
  },
};
</script>

<style scoped>
.plot-container {
  width: 1135px;
  height: 195px;
  border: 1pt solid #eee;
  border-radius: 4px;
}
.plot-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 1135px;
  margin-bottom: 4px;
  padding: 0 3px;
  font-size: 0.85em;
  font-weight: bold;
}
.plot-header-label {
  text-align: left;
}
.plot-header-stats-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.plot-stats-copy-btn {
  background: #fff;
  border: 1px solid #c9b8cf;
  border-radius: 4px;
  color: #6e5875;
  cursor: pointer;
  padding: 3px 6px;
  font-size: 0.9em;
  line-height: 1;
}
.plot-stats-copy-btn:hover {
  color: #4a3d52;
  border-color: #907793;
  background: #faf8fb;
}
.plot-header-stats {
  display: flex;
  flex-wrap: nowrap;
  gap: 12px;
  font-weight: normal;
  text-align: right;
  white-space: nowrap;
}
.cycle-stat {
  font-size: 0.95em;
}
.cycle-stat-clickable {
  cursor: pointer;
}
.cycle-stat-clickable:hover {
  text-decoration: underline;
}
.loading-message {
  padding: 10px;
}
</style>
